'use client';

import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface CsvExportButtonProps {
  label: string;
  exportingLabel: string;
  errorLabel: string;
  onExport: () => Promise<
    { csv: string; filename: string } | { error: string }
  >;
}

export function CsvExportButton({
  label,
  exportingLabel,
  errorLabel,
  onExport,
}: CsvExportButtonProps) {
  const [isPending, setIsPending] = useState(false);

  const handleClick = async () => {
    setIsPending(true);
    try {
      const result = await onExport();
      if ('error' in result) {
        toast.error(result.error || errorLabel);
        return;
      }
      const blob = new Blob(['\uFEFF', result.csv], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed', err);
      toast.error(errorLabel);
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleClick}
      disabled={isPending}
      aria-label={label}
    >
      {isPending ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <Download className="h-4 w-4 mr-2" />
      )}
      {isPending ? exportingLabel : label}
    </Button>
  );
}
